import React from "react";
import { Link } from "react-router-dom";
// import RangeSlider from 'react-range-slider-input';
// import 'react-range-slider-input/dist/style.css';

import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import ImageWithBasePath from "../../core/data/img/ImageWithBasePath";
import { FilterCriteriaObject, FilterCriteriaObjectOperationEnum, FilterOptionObject, PaginatedRequest } from "../../apiClient";

interface ListingSidebarProps {
  filterOptions?: FilterOptionObject[];
  paginateRequest: PaginatedRequest;
  setPaginateRequest: (request: PaginatedRequest) => void;
}

const ListingSidebar = (props: ListingSidebarProps) => {
  // Initialize filterOptions with a default empty array if props.filterOptions is undefined
  let filterOptions: Array<FilterOptionObject> = props.filterOptions ?
    props.filterOptions.filter((option): option is FilterOptionObject => option !== null) : [];

  // Make sure paginateRequest is initialized properly
  let paginateRequest: PaginatedRequest = props.paginateRequest || { 
    page: 0, 
    pageSize: 10, 
    filters: [], 
    sorters: [] 
  };

  // State to hold selected values for each filter key
  const [selectedFilters, setSelectedFilters] = React.useState<Record<string, string[] | number[]>>({});

  // Initialize selectedFilters from paginateRequest on mount or when request changes
  React.useEffect(() => {
    const initialFilters: Record<string, string[] | number[]> = {};
    if (paginateRequest && paginateRequest.filters) {
        paginateRequest.filters.forEach(f => {
             if (f.key && f.values) {
                 // Assuming values is list of strings for text options
                 // For ranges, we might need custom logic if the backend sends them back differently, 
                 // but typically filters in request are "values" or specialized.
                 // For NUMBER_RANGE, the request usually expects min and max. 
                 // Let's assume for now we handle text options here.
                 // The backend uses 'values' for 'In' operation.
                 initialFilters[f.key] = f.values as unknown as string[];
             }
             // Handle Ranges (GreaterThen, LessThen) if needed, but for now let's stick to 'In' for options
             // and handle ranges separately if possible or map them.
             // Currently the backend PaginatedRequest uses simple FilterCriteriaObject.
             // If we used ranges, we might have multiple criteria for same key (>= min, <= max).
             // Let's assume we handle ranges by checking the specific keys.
        });
    }
    setSelectedFilters(initialFilters);
  }, [paginateRequest]);


  const handleCheckboxChange = (filterKey: string, optionValue: string, isChecked: boolean) => {
    const currentValues = (selectedFilters[filterKey] as string[]) || [];
    let updatedValues: string[];
    if (isChecked) {
      updatedValues = [...currentValues, optionValue];
    } else {
      updatedValues = currentValues.filter(item => item !== optionValue);
    }

    const newFilters = { ...selectedFilters, [filterKey]: updatedValues };
    setSelectedFilters(newFilters);
    updatePaginatedRequest(newFilters);
  };

  const handleRangeChange = (filterKey: string, value: number | number[]) => {
      // value is [min, max]
      const newFilters = { ...selectedFilters, [filterKey]: value as number[] };
      setSelectedFilters(newFilters);
      // Debounce or apply immediately? Let's apply immediately for now or adding a "Apply" button? 
      // Typically sliders need debounce. For now, let's use onAfterChange logic if RC slider supports it, 
      // or just update state and have a separate effect or just update request.
      // RC Slider's onChange happens while dragging. onAfterChange happens on drop.
      // We will only render slider if we use onAfterChange to trigger request update.
  };
  
  const onRangeAfterChange = (filterKey: string, value: number | number[]) => {
      const newFilters = { ...selectedFilters, [filterKey]: value as number[] };
      updatePaginatedRequest(newFilters);
  }

  const updatePaginatedRequest = (currentFiltersState: Record<string, string[] | number[]>) => {
      if (!props.setPaginateRequest) return;

      const newFiltersList: FilterCriteriaObject[] = [];

      Object.entries(currentFiltersState).forEach(([key, values]) => {
          if (!values || values.length === 0) return;

          // Check if it is a range (array of 2 numbers) and the key is known for range
          if ((key === 'price' || key === 'features.mileage') && Array.isArray(values) && values.length === 2) {
              // Add a generic range filter?
              // The backend PaginatedRequest usually expects a list of FilterCriteria.
              // We need to know how to send Range filters.
              // Usually FilterCriteriaObject has 'operation' and 'value'.
              // Since existing backend uses FilterCriteriaObject, we might need to send:
              // key, operation: BETWEEN, value: [min, max] ??
              // Looking at FilterOptionUtils.toPredicate, it uses SpecificationUtils.createQuery.
              // We need to guess or know the supported operations. 
              // Assuming FilterCriteriaObjectOperationEnum definitions.
              // If Between is not supported, we send specific logic. 
              // But let's assume standard 'Between' or custom handling.
              // Let's try sending as "Between" if enum exists, or check API client.
              // Checking API client imports... FilterCriteriaObjectOperationEnum.
              
              // If we don't have BETWEEN, we usually send two filters: GreaterThanOrEqual and LessThanOrEqual.
              // Or the backend handles a list of values as range if operation is specific.
              // Let's assume we send two filters for now to be safe if we don't know 'Between'.
              newFiltersList.push({
                  key: key,
                  operation: FilterCriteriaObjectOperationEnum.GreaterOrEqual,
                  value: (values as number[])[0] as any
              });
              newFiltersList.push({
                  key: key,
                  operation: FilterCriteriaObjectOperationEnum.LessOrEqual,
                  value: (values as number[])[1] as any
              });

          } else {
              // Text Options (Checkboxes)
              newFiltersList.push({
                  key: key,
                  operation: FilterCriteriaObjectOperationEnum.In,
                  values: values as unknown as object[]
              });
          }
      });

      const newRequest = {
          ...paginateRequest,
          filters: newFiltersList,
          page: 0 // Reset to first page on filter change
      };
      
      props.setPaginateRequest(newRequest);
  };

  const resetAllFilters = () => {
    setSelectedFilters({});
    props.setPaginateRequest({
        ...paginateRequest,
        filters: [],
        page: 0
    });
  };

  // Helper to render a checkbox section
  const renderCheckboxGroup = (title: string, filterKey: string, accordionId: string) => {
      const optionObj = filterOptions.find(o => o.key === filterKey);
      if (!optionObj || !optionObj.options || optionObj.options.length === 0) return null;

      return (
        <div className="accordion" id={`accordion_${accordionId}`}>
          <div className="card-header-new" id={`heading_${accordionId}`}>
            <h6 className="filter-title">
              <Link
                to="#"
                className="w-100"
                data-bs-toggle="collapse"
                data-bs-target={`#collapse_${accordionId}`}
                aria-expanded="true"
                aria-controls={`collapse_${accordionId}`}
              >
                {title}
                <span className="float-end">
                  <i className="fa-solid fa-chevron-down"></i>
                </span>
              </Link>
            </h6>
          </div>
          <div
            id={`collapse_${accordionId}`}
            className="collapse show"
            aria-labelledby={`heading_${accordionId}`}
          >
            <div className="card-body-chat">
              <div className="row">
                <div className="col-md-12">
                  <div className="selectBox-cont">
                    {optionObj.options.map((option, idx) => {
                       const optionStr = String(option);
                       const isChecked = ((selectedFilters[filterKey] as string[]) || []).includes(optionStr);
                       return (
                        <label key={idx} className="custom_check w-100">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => handleCheckboxChange(filterKey, optionStr, e.target.checked)}
                          />
                          <span className="checkmark" /> {optionStr}
                        </label>
                       );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
  };

  // Helper to render a range slider
  const renderRangeSlider = (title: string, filterKey: string, accordionId: string) => {
      const optionObj = filterOptions.find(o => o.key === filterKey);
      // Check for min/max
      if (!optionObj || optionObj.min === undefined || optionObj.max === undefined) return null;
      // If min == max, no range to slide
      if ((optionObj.min as unknown as number) === (optionObj.max as unknown as number)) return null;

      const currentRange = (selectedFilters[filterKey] as number[]) || [optionObj.min as unknown as number, optionObj.max as unknown as number];

      return (
        <div className="accordion" id={`accordion_${accordionId}`}>
            <div className="card-header-new" id={`heading_${accordionId}`}>
                <h6 className="filter-title">
                <Link
                    to="#"
                    className="w-100"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse_${accordionId}`}
                    aria-expanded="true"
                    aria-controls={`collapse_${accordionId}`}
                >
                    {title}
                    <span className="float-end">
                    <i className="fa-solid fa-chevron-down"></i>
                    </span>
                </Link>
                </h6>
            </div>
            <div
                id={`collapse_${accordionId}`}
                className="collapse show"
            >
                <div className="card-body-chat">
                    <div className="filter-range">
                        <div className="d-flex justify-content-between mb-2">
                             <span>{currentRange[0]}</span>
                             <span>{currentRange[1]}</span>
                        </div>
                        <Slider 
                            range 
                            min={optionObj.min as unknown as number} 
                            max={optionObj.max as unknown as number} 
                            value={currentRange} 
                            onChange={(val) => handleRangeChange(filterKey, val as number | number[])}
                            onAfterChange={(val) => onRangeAfterChange(filterKey, val as number | number[])}
                        />
                    </div>
                </div>
            </div>
        </div>
      );
  };

  return (
    <>
      <form action="#" autoComplete="off" className="sidebar-form">
        <div className="sidebar-heading">
          <h3>Filtre</h3>
        </div>

        {renderCheckboxGroup("Marcă", "make", "make")}
        {renderCheckboxGroup("Model", "model", "model")}
        {renderCheckboxGroup("Combustibil", "features.fuelType", "fuel")}
        {renderCheckboxGroup("An", "features.year", "year")}
        
        {renderRangeSlider("Preț (€)", "price", "price")}
        {renderRangeSlider("Kilometraj (km)", "features.mileage", "mileage")}

        <Link to="#" className="reset-filter" onClick={resetAllFilters}>
          Reseteaza Filtrele
        </Link>
      </form>
    </>
  );
};

// Add default props
ListingSidebar.defaultProps = {
  filterOptions: [],
  paginateRequest: {
    page: 0,
    pageSize: 10,
    filters: [],
    sorters: []
  },
  setPaginateRequest: () => {}
};

export default ListingSidebar;
